// Speech-to-Text Service using Web Speech API and external APIs
import { getApiUrl } from '../helpers/consts';

class SpeechToTextService {
  constructor() {
    this.provider = process.env.REACT_APP_STT_PROVIDER || 'web-speech';
    this.openaiApiKey = process.env.REACT_APP_OPENAI_API_KEY;
    this.isWebSpeechSupported = 'webkitSpeechRecognition' in window || 'SpeechRecognition' in window;
  }

  async transcribeAudio(audioBlob, language = 'ru-RU') {
    try {
      if (this.provider === 'openai' && this.openaiApiKey && this.openaiApiKey !== 'your_openai_api_key_here') {
        return await this.transcribeWithOpenAI(audioBlob, language);
      } else if (this.isWebSpeechSupported) {
        // Web Speech API работает в реальном времени, здесь заглушка для blob
        throw new Error('Web Speech API не поддерживает транскрипцию blob файлов');
      } else {
        throw new Error('Speech-to-Text не поддерживается');
      }
    } catch (error) {
      console.error('Speech-to-Text Error:', error);
      throw error;
    }
  }

  async transcribeWithOpenAI(audioBlob, language) {
    const formData = new FormData();
    formData.append('audio', audioBlob, 'audio.webm');

    // Используем функцию для определения правильного API URL
    const apiUrl = getApiUrl();
    
    const response = await fetch(`${apiUrl}/voice-petitions/transcribe/`, {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Ошибка транскрипции аудио');
    }

    const data = await response.json();
    return data.text;
  }

  // Для реального времени используем Web Speech API
  createWebSpeechRecognition(language = 'ru-RU', onResult, onError, onEnd) {
    if (!this.isWebSpeechSupported) {
      throw new Error('Web Speech API не поддерживается в этом браузере');
    }

    const SpeechRecognition = window.webkitSpeechRecognition || window.SpeechRecognition;
    const recognition = new SpeechRecognition();

    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = language;
    recognition.maxAlternatives = 1;

    recognition.onresult = (event) => {
      let finalTranscript = '';
      let interimTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }

      onResult(finalTranscript, interimTranscript);
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      onError(event.error);
    };

    recognition.onend = () => {
      onEnd();
    };

    return recognition;
  }

  // Проверка поддержки различных возможностей
  getCapabilities() {
    return {
      webSpeechAPI: this.isWebSpeechSupported,
      openaiWhisper: !!(this.openaiApiKey && this.openaiApiKey !== 'your_openai_api_key_here'),
      realtimeTranscription: this.isWebSpeechSupported,
      fileTranscription: !!(this.openaiApiKey && this.openaiApiKey !== 'your_openai_api_key_here')
    };
  }

  // Получение поддерживаемых языков для Web Speech API
  getSupportedLanguages() {
    return [
      { code: 'ru-RU', name: 'Русский' },
      { code: 'ky-KG', name: 'Кыргызский' },
      { code: 'en-US', name: 'English (US)' },
      { code: 'en-GB', name: 'English (UK)' },
      { code: 'es-ES', name: 'Español' },
      { code: 'fr-FR', name: 'Français' },
      { code: 'de-DE', name: 'Deutsch' },
      { code: 'it-IT', name: 'Italiano' },
      { code: 'pt-BR', name: 'Português (Brasil)' },
      { code: 'zh-CN', name: '中文 (简体)' },
      { code: 'ja-JP', name: '日本語' },
      { code: 'ko-KR', name: '한국어' }
    ];
  }
}

const speechToTextService = new SpeechToTextService();
export default speechToTextService;